<template>
  <div class="d-flex flex-column w-100" @keyup.enter="submitForm">
    <div class="pb-1">
      <div v-if="!twoFactorEnabled">
        <transition name="bounce">
          <BushelTextInputMaterial
            type="email"
            name="email"
            autofocus
            :label-active="'Email'"
            :error="submitted && !validateEmail(formData.email)"
            autocomplete="email"
            :value="formData.email"
            :error-text="'Required'"
            @input="$emit('input', { field: 'email', value: $event })"
          />
        </transition>
        <transition name="bounce">
          <div v-if="!showPasswordReset" class="form-group material-form">
            <BushelTextInputMaterial
              :type="showPassword ? 'text' : 'password'"
              name="password"
              :error="submitted && formData.password.length < 8"
              :error-text="'8 char min, letters, mixedCase, numbers, symbols'"
              :label-active="'Password'"
              :value="formData.password"
              :append-icon="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
              :message="'8 char min, letters, mixedCase, numbers, symbols'"
              @icon-clicked="showPassword = !showPassword"
              @input="$emit('input', { field: 'password', value: $event })"
            />
          </div>
        </transition>
      </div>
      <div v-else class="mb-2">
        <BushelTextInputMaterial
          ref="twofa"
          autofocus
          type="number"
          :label-active="'Authy Token'"
          :error="tokenSubmitted && !formData.token"
          :value="formData.token"
          :error-text="'Required'"
          :message="'Please use the Authy App on your phone to login'"
          @input="on2faInput"
        />
      </div>
      <div class="text-center" :style="showPasswordReset ? 'margin-top: 7px' : ''">
        <BushelBtn
          class="btn-block"
          variant="success"
          dusk="signin-button"
          :loading="submitting"
          :disabled="(resetSubmitted && showPasswordReset) || submitting"
          @click="submitForm"
        >
          {{ !showPasswordReset ? "Sign In" : !resetSubmitted ? "Reset" : "Check Your Email" }}
        </BushelBtn>
      </div>

      <div v-if="error && (twoFactorEnabled || !showPasswordReset)" class="alert alert-warning mt-1 mb-0 w-100">
        <div v-if="twoFactorEnabled">2 Factor Code Incorrect</div>
        <div v-else-if="!showPasswordReset">Email or Password Invalid</div>
      </div>

      <div class="mt-2 text-center">
        <BushelBtn class="mt-2 icon-btn" variant="grey" ripple-color="grey" flat @click="goToSocialMedia('facebook')">
          <i class="fab fa-facebook-f fa-fw" />
          <span class="sr-only"> Facebook </span>
        </BushelBtn>
        <BushelBtn class="mt-2 icon-btn" variant="grey" ripple-color="grey" flat @click="goToSocialMedia('twitter')">
          <i class="fab fa-twitter fa-fw" />
          <span class="sr-only"> Twitter </span>
        </BushelBtn>
        <BushelBtn class="mt-2 icon-btn" variant="grey" ripple-color="grey" flat @click="goToSocialMedia('instagram')">
          <i class="fab fa-instagram fa-fw" />
          <span class="sr-only"> Instagram </span>
        </BushelBtn>
        <BushelBtn class="mt-2 icon-btn" variant="grey" ripple-color="grey" flat @click="goToSocialMedia('linkedin')">
          <i class="fab fa-linkedin-in fa-fw" />
          <span class="sr-only"> LinkedIn </span>
        </BushelBtn>
        <BushelBtn class="mt-2 icon-btn" variant="grey" ripple-color="grey" flat @click="goToSocialMedia('website')">
          <i class="fas fa-globe fa-fw" />
          <span class="sr-only"> Website </span>
        </BushelBtn>
      </div>
    </div>
    <div v-if="!twoFactorEnabled">
      <BushelBtn class="mt-3 btn-sm m-1" variant="secondary" ripple-color="grey" flat @click="showResetPassword">
        {{ showPasswordReset ? "Back" : "Forgot Password" }}
      </BushelBtn>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import * as api from "@/services/api";
import BushelTextInputMaterial from "@/components/common/forms/BushelTextInputMaterial.vue";
import { validateEmail } from "@/services/helpers";

export default {
  components: { BushelTextInputMaterial },
  props: {
    formData: { type: Object, required: true },
  },
  emits: ["input"],
  data() {
    return {
      appEnv: APP_ENVIRONMENT,
      industry: APP_INDUSTRY,
      showPasswordReset: false,
      showPassword: false,
      submitted: false,
      submitting: false,
      resetSubmitted: false,
      error: false,
      errorMessage: null,
      twoFactorEnabled: false,
      tokenSubmitted: false,
    };
  },
  computed: {
    ...mapState("auth", {
      user: (state) => state.user,
    }),
  },
  methods: {
    ...mapActions({
      loginAction: "auth/login",
      resetPasswordAction: "auth/resetPassword",
    }),
    validateEmail,
    resetPassword() {
      this.submitted = true;
      if (this.validateEmail(this.formData.email)) {
        this.submitting = true;
        this.resetSubmitted = true;
        this.error = false;
        this.errorMessage = null;
        api.post("/auth/password/resetEmail", { email: this.formData.email }).then((response) => {
          this.submitting = false;
        });
      }
    },
    showResetPassword() {
      this.showPasswordReset = !this.showPasswordReset;
    },
    on2faInput($event) {
      this.$emit("input", { field: "token", value: $event });
      if ($event.length >= 7) this.submitForm();
    },
    submitForm() {
      if (!this.submitting) {
        this.showPasswordReset ? this.resetPassword() : this.signIn();
      }
    },
    signIn() {
      this.submitted = true;
      this.error = false;
      this.errorMessage = null;
      if (this.validateEmail(this.formData.email) && this.formData.password.length >= 8) {
        this.submitting = true;
        const payload = { email: this.formData.email, token: this.formData.token, password: this.formData.password };
        this.loginAction(payload).then((response) => {
          if (response == "success") {
            if (this.formData.buyerInvitation) {
              this.$router.replace(
                `/buyer-onboard?buyer_invitation=${this.$route.query.buyer_invitation}&invitation_email=${this.$route.query.invitation_email}`,
              );
            } else if (this.formData.brandInvitation) {
              this.$router.push(
                `/brand-onboard?brand-invitation=${this.$route.query["brand-invitation"]}&invitation_email=${this.$route.query.invitation_email}`,
              );
            } else if (this.formData.orderUuid) {
              this.handleRoutingToOrder(this.formData.orderUuid);
            } else {
              this.$router.replace("/");
            }
          } else if (response === "two factor enabled") {
            this.twoFactorEnabled = true;
            this.submitting = false;
          } else {
            this.error = true;
            this.errorMessage =
              response.response && response.response.data && response.response.data.message
                ? response.response.data.message
                : "There was an error logging in. Please try again.";
            this.submitting = false;
          }
        });
      }
    },
    handleRoutingToOrder(orderUUID) {
      api.get(`invoice/${orderUUID}`).then((response) => {
        if (response.data && response.data.id) {
          const order = response.data;

          if (order && this.user.current_company_id == order.seller_company_id) {
            this.$router.replace(`/orders/shipping/${orderUUID}`);
          } else if (order && (!order.buyer_company_id || this.user.current_company_id == order.buyer_company_id)) {
            this.$router.replace(`/orders/receiving/${orderUUID}`);
          } else {
            // final catch if didn't work above
            this.$router.replace("/");
          }
        }
      });
    },

    fetchOrder(orderUUID) {
      return api.get(`invoice/${orderUUID}`);
    },

    goToSocialMedia(type) {
      if (type == "facebook") {
        if (this.industry == "cannabis") {
          window.open("https://www.facebook.com/ApexTradingWholesale/");
        } else if (this.industry == "hemp") {
          window.open("https://www.facebook.com/Bushel44-114084966629888/");
        }
      }
      if (type == "instagram") {
        if (this.industry == "cannabis") {
          window.open("https://www.instagram.com/apextradinghq/");
        } else if (this.industry == "hemp") {
          window.open("https://www.instagram.com/bushel44_co/");
        }
      }
      if (type == "twitter") {
        if (this.industry == "cannabis") {
          window.open("https://twitter.com/apextrading_co");
        } else if (this.industry == "hemp") {
          window.open("https://twitter.com/bushel44");
        }
      }
      if (type == "linkedin") {
        if (this.industry == "cannabis") {
          window.open("https://www.linkedin.com/company/apex-trading");
        } else if (this.industry == "hemp") {
          window.open("https://www.linkedin.com/company/bushel44");
        }
      }
      if (type == "website") {
        if (this.industry == "cannabis") {
          window.open("https://www.apextrading.com");
        } else if (this.industry == "hemp") {
          window.open("https://www.bushel44.com/");
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.bounce-enter-active {
  animation: bounce-in 0.5s;
}
.bounce-leave-active {
  animation: bounce-in 0.5s reverse;
}
@keyframes bounce-in {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
